import React, { useState, useEffect, useCallback  } from 'react';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import { Table, Input, DatePicker, Button, Modal, message, Tag, Select } from 'antd';
import { PaperClipOutlined, DeleteOutlined, ReloadOutlined, FileExcelOutlined, ClearOutlined  } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import ReceiptModal from './ReceiptModal';

const { Search } = Input;
const { MonthPicker } = DatePicker;

// Payment method mapping with color codes
const PAYMENT_METHODS = {
  'credit_card': { label: 'Credit Card', color: 'blue' },
  'cash': { label: 'Cash', color: 'green' },
  'bank_transfer': { label: 'Bank Transfer', color: 'purple' }
};

const PaymentDetails = () => {
  const { t } = useTranslation();
  const [paymentDetails, setPaymentDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonthYear, setSelectedMonthYear] = useState(null);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [paymentMethodFilter, setPaymentMethodFilter] = useState(null);
  const [bankFilter, setBankFilter] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL;

  // Helper function to format payment method
  const formatPaymentMethod = (method) => {
    return PAYMENT_METHODS[method]?.label || method;
  };

  // Helper function to get payment method color
  const getPaymentMethodColor = (method) => {
    return PAYMENT_METHODS[method]?.color || 'default';
  };

  const fetchPaymentDetails = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/payment/allpayments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const formattedData = response.data.map(payment => ({
        ...payment,
        // Keep the original payment method for the Tag rendering
        formatted_payment_method: formatPaymentMethod(payment.payment_method)
      }));
      
      setPaymentDetails(formattedData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching payment details:', error);
      setLoading(false);
    }
  }, [API_URL]); 

  useEffect(() => {
    fetchPaymentDetails(); 
  }, [fetchPaymentDetails]); 

  const handleDeletePayment = async (id) => {
    Modal.confirm({
      title: t('confirmDeletePayment'),
      content: t('deletePaymentWarning'),
      okText: t('yes'),
      cancelText: t('no'),
      onOk: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`${API_URL}/payment/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          message.success(t('paymentDeleted'));
          fetchPaymentDetails(); // Refresh data after deletion
        } catch (error) {
          console.error('Error deleting payment:', error);
          message.error(t('deleteError'));
        }
      },
    });
  };

  const handleSearch = (value) => {
    setSearchTerm(value.toLowerCase());
  };

  const handleMonthYearChange = (date) => {
    setSelectedMonthYear(date ? date.format('YYYY-MM') : null);
  };


  const openReceiptModal = (record) => {
    setSelectedReceipt(record);
    setIsReceiptModalOpen(true);
  };

  const closeReceiptModal = () => {
    setIsReceiptModalOpen(false);
    setSelectedReceipt(null);
  };

  const filteredPaymentDetails = paymentDetails.filter((payment) => {
  const matchesSearch =
    payment.invoice_no?.toLowerCase().includes(searchTerm) ||
    payment.firstname?.toLowerCase().includes(searchTerm) ||
    payment.lastname?.toLowerCase().includes(searchTerm) ||
    payment.class_code?.toLowerCase().includes(searchTerm);

  const matchesMonthYear =
    !selectedMonthYear || moment(payment.timestamp).format('YYYY-MM') === selectedMonthYear;

  const matchesMethod =
    !paymentMethodFilter || payment.payment_method === paymentMethodFilter;

  const matchesBank =
    !bankFilter || payment.bank_details?.bank_name === bankFilter;

  return matchesSearch && matchesMonthYear && matchesMethod && matchesBank;
});
const clearFilters = () => {
  setSearchTerm('');
  setSelectedMonthYear(null);
  setPaymentMethodFilter('');
  setBankFilter('');
};


  const columns = [
    {
      title: t('invoiceNo'),
      dataIndex: 'invoice_no',
      key: 'invoice_no',
    },
    {
        title: t('receiptNo'),
        dataIndex: 'receipt_no',
        key: 'receipt_no',
        render: (text) => text || '-', // fallback if null
      },
    {
      title: t('student'),
      key: 'name',
      render: (_, record) => `${record.firstname || ''} ${record.lastname || ''}`.trim(),
    },
    {
      title: t('course'),
      dataIndex: 'class_code',
      key: 'class_code',
    },
    {
      title: t('paymentMethod'),
      dataIndex: 'payment_method',
      key: 'payment_method',
      render: (method) => (
        <Tag color={getPaymentMethodColor(method)}>
          {formatPaymentMethod(method)}
        </Tag>
      ),
    },
    {
  title: t('Bank Details'),
  dataIndex: 'bank_details',
  key: 'bank_details',
  width: 200,
  render: (bankDetails, record) => {
    // Only show bank details for bank transfers
    if (record.payment_method !== 'bank_transfer') {
      return (
        <span style={{ color: '#999', fontSize: '12px' }}>
          {t('N/A')}
        </span>
      );
    }

    // If no bank details available
    if (!bankDetails) {
      return (
        <Tag color="red">
          {t('No Bank Info')}
        </Tag>
      );
    }

    return (
      <div style={{ fontSize: '12px' }}>
        <div style={{ fontWeight: 'bold', color: '#1890ff' }}>
          {bankDetails.bank_name}
        </div>
        <div style={{ color: '#666' }}>
          {bankDetails.account_number}
        </div>
        <div style={{ color: '#999', fontSize: '11px' }}>
          {bankDetails.account_name}
        </div>
        {bankDetails.bank_branch && (
          <div style={{ color: '#999', fontSize: '10px' }}>
            {bankDetails.bank_branch}
          </div>
        )}
      </div>
    );
  },
},
    {
      title: t('subtotal'),
      dataIndex: 'final_price',
      key: 'final_price',
      render: (finalPrice) => (finalPrice != null ? finalPrice.toLocaleString() : '0'),
    },
    {
      title: t('nettotal'),
      dataIndex: 'pay_amount',
      key: 'pay_amount',
      render: (amount) => (
        <span style={{ color: 'green' }}>
          {amount != null ? amount.toLocaleString() : '0'}
        </span>
      ),
    },
    {
      title: t('remaining'),
      dataIndex: 'remaining_balance',
      key: 'remaining_balance',
      render: (balance) => (
        <span style={{ color: balance === 0 ? 'green' : 'red' }}>
          {balance != null ? balance.toLocaleString() : '0'}
        </span>
      ),
    },
    {
  title: t('at'),
  dataIndex: 'timestamp',
  key: 'timestamp',
  render: (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);

    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' }); // e.g., "Jul"
    const year = String(date.getFullYear()).slice(-2); // last 2 digits
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day} ${month} ${year} ${hours}:${minutes}`;
  },
},
    {
      title: t('memo'),
      dataIndex: 'memo',
      key: 'memo',
    },
    {
      title: t('receipt'),
      key: 'receipt',
      render: (_, record) => (
        <>
          <Button 
            type="primary"
            icon={<PaperClipOutlined />}
            onClick={() => openReceiptModal(record)}
            style={{ marginRight: '8px' }}
          />
        </>
      ),
    },
    {
      title: t('actions'),
      key: 'actions',
      render: (_, record) => (
        <>
          <Button 
            type="primary"
            icon={<DeleteOutlined />}
            danger 
            onClick={() => handleDeletePayment(record.id)}
          />
        </>
      ),
    },
  ];

  const csvData = filteredPaymentDetails.map((payment) => ({
  invoiceNo: payment.invoice_no,
  receiptNo: payment.receipt_no || '',
  firstName: payment.firstname,
  lastName: payment.lastname,
  subject: payment.class_code,
  paymentMethod: formatPaymentMethod(payment.payment_method),
  bankName: payment.bank_details?.bank_name || '',
  bankAccount: payment.bank_details?.account_number || '',
  subtotal: payment.final_price || 0,
  nettotal: payment.pay_amount || 0,
  remaining: payment.remaining_balance || 0,
  memo: payment.memo || '',
  timestamp: new Date(payment.timestamp).toLocaleString(),
}));

  return (
    <div className="payment-details-page">
      <div style={{ marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
          <Search
            placeholder={t('Search by invoice no. or student name')}
            onSearch={handleSearch}
            style={{ width: 350 }}
            allowClear
          />
          <MonthPicker
            placeholder={t('Select Month/Year')}
            onChange={handleMonthYearChange}
            style={{ width: 200 }}
          />
          <Select
            placeholder={t('Select Payment Method')}
            onChange={(val) => setPaymentMethodFilter(val)}
            allowClear
            style={{ width: 200 }}
            options={Object.entries(PAYMENT_METHODS).map(([key, { label }]) => ({
              label,
              value: key,
            }))}
          />
          <Select
            placeholder={t('Select Bank')}
            onChange={(val) => setBankFilter(val)}
            allowClear
            style={{ width: 280 }}
            options={[
              ...new Set(paymentDetails.map(p => p.bank_details?.bank_name))
            ]
              .filter(Boolean)
              .map(name => ({ label: name, value: name }))
            }
          />
          <Button onClick={clearFilters} icon={<ClearOutlined />}>{t('Clear Filter')}</Button>
          <Button icon={<ReloadOutlined />}  onClick={fetchPaymentDetails}>{t('refresh')}</Button>
          <CSVLink data={csvData} filename="payment_details.csv" target="_blank">
            <Button icon={<FileExcelOutlined />}>
              {t('exportCSV')}
            </Button>
          </CSVLink>
        </div>

      {loading ? (
        <p>{t('loading')}</p>
      ) : (
        <Table dataSource={filteredPaymentDetails} columns={columns} rowKey="id" pagination={{ pageSize: 50 }} />
      )}

      {isReceiptModalOpen && <ReceiptModal show={isReceiptModalOpen} onClose={closeReceiptModal} registration={selectedReceipt} />}
    </div>
  );
};

export default withRoleAccess(PaymentDetails, ['superadmin',]);